import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export const AppShell = ({ nav, title, subtitle, actions, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    const target = user?.role === "admin" ? "/admin" : "/partner";
    await logout();
    navigate(target);
  };

  const links = (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setOpen(false)}
          data-testid={item.testId}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-left">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white px-5 py-6 lg:flex">
        <Link to="/">
          <Brand />
        </Link>
        <div className="mt-8 flex-1">{links}</div>
        <div className="border-t border-slate-200 pt-4">
          <div className="mb-3 px-1">
            <div className="truncate text-sm font-medium text-slate-900">{user?.name}</div>
            <div className="truncate text-xs text-slate-500">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={doLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-white/85 px-5 py-4 backdrop-blur-md lg:px-10">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open navigation"
              data-testid="mobile-nav-open"
            >
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <div>
              <h1 className="font-display text-xl font-bold text-slate-900 md:text-2xl">{title}</h1>
              {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>

        {open && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-72 bg-white p-5">
              <div className="mb-6 flex items-center justify-between">
                <Brand />
                <button onClick={() => setOpen(false)} aria-label="Close navigation">
                  <X className="h-5 w-5 text-slate-600" />
                </button>
              </div>
              {links}
              <Button
                variant="outline"
                size="sm"
                className="mt-6 w-full justify-start gap-2"
                onClick={doLogout}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </Button>
            </div>
          </div>
        )}

        <main className="px-5 py-7 lg:px-10 lg:py-9">{children}</main>
      </div>
    </div>
  );
};
